/**
 * ether5Card - progressive-disclosure selection card
 *
 * A card that carries enough at a glance to make a selection decision, and
 * discloses the rest on hover, focus, or selection. Designed to tile in an
 * <ether5-grid> quilt, including inside a chat bubble.
 *
 *   <ether5-grid data-ether5-gap="12">
 *     <ether5-card
 *       data-ether5-shape="WIDE"
 *       data-ether5-caption="Survey"
 *       data-ether5-title="Gain user insight with a dynamic survey"
 *       data-ether5-desc="Easily create a survey that drives better decisions."
 *       data-ether5-token="ethercard_survey"></ether5-card>
 *   </ether5-grid>
 *
 * ATTRIBUTES
 *   data-ether5-caption  top row text (black band)
 *   data-ether5-title    bottom row heading (white band)
 *   data-ether5-desc     bottom row body
 *   data-ether5-shape    SQUARE (default) | WIDE | TALL
 *   data-ether5-gap      px; normally inherited from the grid, override per card
 *   data-ether5-token    entity identity, echoed on every event
 *   data-ether5-prompt   text to send as a conversation turn on select (optional)
 *   selected             boolean; reflects selection, keeps the detail disclosed
 *
 * These map 1:1 onto `sseEtherCard.name` = {caption, tagline, desc} - `title`
 * is `tagline`. Swapping mock data for the real table is a field rename.
 *
 * SHAPES
 * Sizing is the grid's job, not the card's: the quilt's rows are exactly as tall
 * as its columns are wide, so a span already resolves to the requested geometry.
 *   SQUARE  1x1  -> height == width
 *   WIDE    2x1  -> width  == 2 * height + gap   (the spanned gap is included)
 *   TALL    1x2  -> height == 2 * width  + gap
 *
 * NOT `data-n55-prompt`. That attribute is claimed by <m5t-chat-workflow>'s own
 * delegated click handler; a card carrying it inside a workflow bubble would
 * publish twice - once from the card, once from the bubble - because the click
 * retargets to the card host on its way out of the shadow tree.
 *
 * ICONIC VERBS
 * Single-tap actions on the underlying entity (pin, fav, note, thumbs, CRUD).
 * The disclosed panel reserves a row and exposes `<slot name="verbs">`; anything
 * slotted in that dispatches `ether5-verb` is forwarded. Icons land later.
 *
 * NOT A BUTTON
 * The card is an entity container, not a control. Nothing here is a <button> -
 * the interactive elements are the things placed *inside* it (verbs today,
 * more later), and a container that was itself a button could not legally or
 * accessibly hold them. Selection is a click on the face, handled as plain
 * interaction rather than borrowed button semantics.
 */

const SHAPES = new Set(['SQUARE', 'WIDE', 'TALL']);

const STYLES = `
  :host {
    /* Definite block size comes from the grid row span, which container-type:
       size requires. With it, cq units below resolve against the card itself. */
    container-type: size;

    display: block;
    position: relative;
    overflow: hidden;
    border-radius: 6px;
    background: var(--e5-cap-bg, #000);
    box-shadow: 0 2px 6px rgb(0 0 0 / 0.35);
    transition: box-shadow 0.3s ease, outline-color 0.3s ease;
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  :host([data-ether5-shape="WIDE"]) {
    /* --e5-span-w drops to 1 when the grid is down to a single column, where a
       2-column span would overflow. Inherited across the shadow boundary. */
    grid-column: span var(--e5-span-w, 2);
  }

  :host([data-ether5-shape="TALL"]) { grid-row: span 2; }

  :host([selected]) {
    outline-color: var(--e5-accent, #edba08);
    box-shadow: 0 4px 12px rgb(0 0 0 / 0.5);
  }

  /* ---- resting face: two rows, 50% each ---- */

  .face {
    box-sizing: border-box;
    display: grid;
    grid-template-rows: 50% 50%;
    inline-size: 100%;
    block-size: 100%;
    cursor: pointer;
  }

  .cap, .body {
    min-block-size: 0;   /* a grid item will not shrink below content without this */
    overflow: hidden;
    padding: 6cqmin 7cqmin;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .cap  { background: var(--e5-cap-bg, #000);   color: var(--e5-cap-fg, #fff); }
  .body { background: var(--e5-body-bg, #fff);  color: var(--e5-body-fg, #000); gap: 2cqmin; }

  /* Type scales with the card's SHORT axis (cqmin), so a WIDE card does not get
     wall-sized text and a TALL one does not get hairline text. */
  .caption {
    font-size: clamp(0.75rem, 11cqmin, 1.6rem);
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .title {
    font-size: clamp(0.7rem, 7cqmin, 1.05rem);
    font-weight: 600;
    line-height: 1.25;
    text-wrap: pretty;
  }

  .desc {
    font-size: clamp(0.62rem, 5.2cqmin, 0.9rem);
    line-height: 1.35;
    opacity: 0.78;
  }

  /* Multi-line ellipsis. The -webkit- prefixed form is the interoperable one;
     the unprefixed properties are listed after for engines that have moved on. */
  .clamp {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--e5-lines, 2);
    line-clamp: var(--e5-lines, 2);
    overflow: hidden;
  }

  .title { --e5-lines: 2; }
  .desc  { --e5-lines: 2; }

  /* ---- disclosed detail ---- */

  .detail {
    position: absolute;
    inset: 0;
    background: var(--e5-body-bg, #fff);
    color: var(--e5-body-fg, #000);
    padding: 7cqmin;
    display: flex;
    flex-direction: column;
    gap: 3cqmin;
    /* transform only - no layout or paint work while it animates */
    transform: translateY(100%);
    transition: transform 0.28s cubic-bezier(0.2, 0, 0, 1);
    pointer-events: none;
  }

  .detail .desc { --e5-lines: 8; opacity: 0.86; }
  .detail .title { --e5-lines: 3; }

  /* Hover-disclosure only where hovering is real. On touch, tapping selects,
     and [selected] discloses - otherwise a tap leaves a card stuck open. */
  @media (hover: hover) {
    :host(:hover) .detail { transform: translateY(0); pointer-events: auto; }
  }

  :host(:focus-within) .detail,
  :host([selected]) .detail { transform: translateY(0); pointer-events: auto; }

  @media (prefers-reduced-motion: reduce) {
    .detail { transition: none; }
  }

  /* ---- iconic verbs ---- */

  .verbs {
    margin-block-start: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 2cqmin;
    align-items: center;
    min-block-size: 0;
  }

  ::slotted([slot="verbs"]) { display: contents; }

  .verbs:empty { display: none; }
`;

class Ether5Card extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._built = false;
  }

  static get observedAttributes() {
    return [
      'data-ether5-caption',
      'data-ether5-title',
      'data-ether5-desc',
      'data-ether5-gap',
      'selected'
    ];
  }

  connectedCallback() {
    this.build();
    this.paint();

    if (!this.hasAttribute('data-ether5-shape')) {
      this.setAttribute('data-ether5-shape', 'SQUARE');
    }
    if (!this.hasAttribute('role')) this.setAttribute('role', 'group');
  }

  attributeChangedCallback() {
    if (this._built) this.paint();
  }

  get shape() {
    const s = (this.getAttribute('data-ether5-shape') || 'SQUARE').toUpperCase();
    return SHAPES.has(s) ? s : 'SQUARE';
  }

  get selected() { return this.hasAttribute('selected'); }

  set selected(on) {
    this.toggleAttribute('selected', !!on);
  }

  get caption() { return this.getAttribute('data-ether5-caption') || ''; }
  get title()   { return this.getAttribute('data-ether5-title') || ''; }
  get desc()    { return this.getAttribute('data-ether5-desc') || ''; }
  get token()   { return this.getAttribute('data-ether5-token') || ''; }

  build() {
    if (this._built) return;

    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <div class="face" part="face">
        <span class="cap"><span class="caption"></span></span>
        <span class="body">
          <span class="title clamp"></span>
          <span class="desc clamp"></span>
        </span>
      </div>
      <div class="detail" part="detail">
        <span class="title clamp"></span>
        <span class="desc clamp"></span>
        <div class="verbs"><slot name="verbs"></slot></div>
      </div>
    `;

    this._face = this.shadowRoot.querySelector('.face');
    this._built = true;

    this._face.addEventListener('click', this.handleSelect);

    // Verbs are authored by the consumer and slotted in; forwarding their clicks
    // from one place means a verb never has to know about the event contract.
    this.shadowRoot.querySelector('.verbs').addEventListener('click', this.handleVerb);
  }

  paint() {
    const gap = this.getAttribute('data-ether5-gap');
    if (gap) this.style.setProperty('--e5-gap', /^\d+$/.test(gap) ? `${gap}px` : gap);

    for (const el of this.shadowRoot.querySelectorAll('.caption')) el.textContent = this.caption;
    for (const el of this.shadowRoot.querySelectorAll('.title')) el.textContent = this.title;
    for (const el of this.shadowRoot.querySelectorAll('.desc')) el.textContent = this.desc;

    // Labelled on the container, since the container is the entity. No
    // aria-pressed - that belongs to a control, and this is not one.
    this.setAttribute('aria-label', [this.caption, this.title].filter(Boolean).join(' - ') || 'Card');
  }

  handleSelect = (e) => {
    e.preventDefault();
    this.selected = !this.selected;
    this.paint();

    this.emit('ether5-select', {
      selected: this.selected,
      // Only on the way in. Carrying it on deselect too would publish a second
      // identical turn every time a card is switched off.
      message: this.selected ? this.getAttribute('data-ether5-prompt') || undefined : undefined
    });
  };

  handleVerb = (e) => {
    const el = e.target?.closest?.('[data-ether5-verb]');
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    this.emit('ether5-verb', { verb: el.getAttribute('data-ether5-verb') });
  };

  /**
   * `composed: true` is required - without it the event dies at this card's
   * shadow boundary and never reaches the app's web-component event bridge.
   * `pubsubTopic` is what that bridge forwards on; omitting it makes the event
   * inert rather than broken, so a card with no topic is still valid.
   */
  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, {
      bubbles: true,
      composed: true,
      detail: {
        pubsubTopic: this.getAttribute('data-ether5-topic') || undefined,
        token: this.token,
        caption: this.caption,
        title: this.title,
        shape: this.shape,
        ...detail
      }
    }));
  }
}

customElements.define('ether5-card', Ether5Card);

export default Ether5Card;
